/**
 * Official MAP merge tag catalog for the Unlayer email builder.
 *
 * Structure: nested objects → Unlayer renders them as grouped sections
 * in the merge tag picker panel.
 *
 * Notation: dot-path {{group.field}} — matches the backend renderer's
 * resolvePath() which supports deep dot traversal.
 */

export const UNLAYER_MERGE_TAGS = {
  customer: {
    name: 'Customer',
    mergeTags: {
      firstName: {
        name: 'First name',
        value: '{{customer.firstName}}',
        sample: 'Jane',
      },
      lastName: {
        name: 'Last name',
        value: '{{customer.lastName}}',
        sample: 'Doe',
      },
      fullName: {
        name: 'Full name',
        value: '{{customer.fullName}}',
        sample: 'Jane Doe',
      },
      email: {
        name: 'Email address',
        value: '{{customer.email}}',
        sample: 'jane@example.com',
      },
      phone: {
        name: 'Phone number',
        value: '{{customer.phone}}',
        sample: '+39 333 1234567',
      },
    },
  },

  loyalty: {
    name: 'Loyalty',
    mergeTags: {
      pointsBalance: {
        name: 'Points balance',
        value: '{{loyalty.pointsBalance}}',
        sample: '1 250',
      },
      tierCode: {
        name: 'Tier code',
        value: '{{loyalty.tierCode}}',
        sample: 'gold',
      },
      tierName: {
        name: 'Tier name',
        value: '{{loyalty.tierName}}',
        sample: 'Gold',
      },
      lifetimePoints: {
        name: 'Lifetime points',
        value: '{{loyalty.lifetimePoints}}',
        sample: '4 800',
      },
    },
  },

  event: {
    name: 'Event / Order',
    mergeTags: {
      orderId: {
        name: 'Order ID',
        value: '{{event.orderId}}',
        sample: 'ORD-12345',
      },
      orderTotal: {
        name: 'Order total',
        value: '{{event.orderTotal}}',
        sample: '€ 79.90',
      },
      orderDate: {
        name: 'Order date',
        value: '{{event.orderDate}}',
        sample: '15 Mar 2026',
      },
      itemCount: {
        name: 'Item count',
        value: '{{event.itemCount}}',
        sample: '3',
      },
      productName: {
        name: 'Product name',
        value: '{{event.productName}}',
        sample: 'Premium Plan',
      },
      couponCode: {
        name: 'Coupon / discount code',
        value: '{{event.couponCode}}',
        sample: 'SAVE20',
      },
      expiryDate: {
        name: 'Offer expiry date',
        value: '{{event.expiryDate}}',
        sample: '31 Mar 2026',
      },
      trackingUrl: {
        name: 'Tracking URL',
        value: '{{event.trackingUrl}}',
        sample: 'https://track.example.com/ORD-12345',
      },
    },
  },

  system: {
    name: 'System',
    mergeTags: {
      unsubscribeUrl: {
        name: 'Unsubscribe URL',
        value: '{{system.unsubscribeUrl}}',
        sample: 'https://app.example.com/unsubscribe/abc123',
      },
      currentYear: {
        name: 'Current year',
        value: '{{system.currentYear}}',
        sample: '2026',
      },
      companyName: {
        name: 'Company name',
        value: '{{system.companyName}}',
        sample: 'YourBrand',
      },
    },
  },
} as const;

// ─── Flat catalog — used by extractVars() to suggest sample values in preview ─

export const MERGE_TAG_SAMPLES: Record<string, string> = {
  'customer.firstName': 'Jane',
  'customer.lastName': 'Doe',
  'customer.fullName': 'Jane Doe',
  'customer.email': 'jane@example.com',
  'customer.phone': '+39 333 1234567',
  'loyalty.pointsBalance': '1 250',
  'loyalty.tierCode': 'gold',
  'loyalty.tierName': 'Gold',
  'loyalty.lifetimePoints': '4 800',
  'event.orderId': 'ORD-12345',
  'event.orderTotal': '€ 79.90',
  'event.orderDate': '15 Mar 2026',
  'event.itemCount': '3',
  'event.productName': 'Premium Plan',
  'event.couponCode': 'SAVE20',
  'event.expiryDate': '31 Mar 2026',
  'event.trackingUrl': 'https://track.example.com/ORD-12345',
  'system.unsubscribeUrl': 'https://app.example.com/unsubscribe/abc123',
  'system.currentYear': '2026',
  'system.companyName': 'YourBrand',
};
